import React from 'react';
import { useParams } from 'react-router-dom';
import { useDashboardPermissions } from '@components/utils/DashboardPermissions';
import { ButtonClean, PriceString, ProfitString, useModalContext } from '@fourthwall/components';
import { TimezonedDateString } from '../../../../../../../../Timezone/components/TimezonedDateString';
import { GiveawayCostsBreakdown, useGiveawayContributionCostsBreakdownQuery, } from '../modules/GiveawayCostsBreakdown';
import { GiveawayTwitchRedo, useGiveawayGiftsQuery } from '../modules/GiveawayTwitchRedo';
import { useGiveawayContributionQuery } from '../queries/useGiveawayContributionQuery';
import { useGiveawayBanners } from './useGiveawayBanners';
export const useGiveaway = () => {
    const { giveawayId = '' } = useParams();
    const { hasPermission } = useDashboardPermissions();
    const { giveawayContributionQuery } = useGiveawayContributionQuery([{ giveawayId }]);
    const banners = useGiveawayBanners();
    const { open } = useModalContext();
    const giveawayData = giveawayContributionQuery.data;
    // NOTE: We use costs breakdown query hook to prefetch breakdown data before displaying the modal.
    useGiveawayContributionCostsBreakdownQuery([{ giveawayId }]);
    // NOTE: We use costs gifts query hook to prefetch gifts data before displaying the modal.
    useGiveawayGiftsQuery([{ giveawayId }]);
    const handleBreakdownShowClick = () => {
        open(GiveawayCostsBreakdown, { giveawayId });
    };
    const handleRedoGiveawayClick = () => {
        open(GiveawayTwitchRedo, { giveawayId });
    };
    const getItemsSummary = () => {
        if (!giveawayData)
            return;
        return [
            {
                label: 'Subtotal (1 item)', // it's always one item
                value: React.createElement(PriceString, { ...giveawayData.amounts.subtotal }),
            },
            {
                label: 'Pre-paid shipping',
                value: React.createElement(PriceString, { ...giveawayData.amounts.shippingBuffer }),
                tooltip: "Gifter covers the average shipping costs so that a winner doesn't have to pay anything when redeeming the gift.",
            },
            {
                label: 'Taxes',
                value: React.createElement(PriceString, { ...giveawayData.amounts.tax }),
            },
            {
                label: 'Total',
                value: React.createElement(PriceString, { ...giveawayData.amounts.total }),
            },
        ];
    };
    const getSummary = () => {
        if (!giveawayData)
            return;
        const withProfit = hasPermission('contributions.profit');
        return [
            {
                label: 'Net paid by supporter',
                value: React.createElement(PriceString, { ...giveawayData.amounts.paidBySupporter }),
            },
            withProfit
                ? {
                    label: 'Your profit',
                    value: (React.createElement(React.Fragment, null,
                        React.createElement("span", { style: { marginRight: 8, fontSize: 12, fontWeight: 400 } },
                            React.createElement(ButtonClean, { label: "Show breakdown", appearance: "secondary", onClick: handleBreakdownShowClick })),
                        React.createElement(ProfitString, { ...giveawayData.amounts.profit }))),
                }
                : undefined,
        ].filter(Boolean);
    };
    const getToolbarDropdownItems = () => {
        if (!giveawayContributionQuery.data)
            return [];
        const dropdownItems = [];
        if (hasPermission('contributions.orders.redoGiveaway')) {
            dropdownItems.push({
                label: 'Redo giveaway on Twitch',
                onClick: handleRedoGiveawayClick,
            });
        }
        return dropdownItems;
    };
    return {
        giveaway: {
            title: giveawayData
                ? `${giveawayData.supporter.email} order #${giveawayData.friendlyId}`
                : '',
            date: giveawayData ? (React.createElement(TimezonedDateString, { date: giveawayData.createdAt, format: "[Placed] MMM D, YYYY [at] h:mm A" })) : undefined,
            banners,
            details: giveawayData
                ? {
                    email: giveawayData.supporter.email,
                }
                : undefined,
            items: giveawayData
                ? [
                    {
                        offerId: giveawayData.offer.offerId,
                        title: `Gift: ${giveawayData.offer.productName}`,
                        image: giveawayData.offer.imgUrl,
                        quantity: giveawayData.offer.quantity,
                        price: giveawayData.offer.unitPrice,
                    },
                ]
                : undefined,
            itemsSummary: getItemsSummary(),
            summary: getSummary(),
            toolbarDropdownItems: getToolbarDropdownItems(),
            message: giveawayContributionQuery.data?.supporter.message,
            isLoading: giveawayContributionQuery.isLoading,
        },
    };
};
